import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";

/**
 * Reusable receipt input row.
 * Uploads the picked file to /costing/project/{pid}/receipts and keeps
 * { file_id, name, mime, size } locally so the parent form can attach it
 * to the eventual transport/installation expense entry.
 */
export default function ReceiptField({ p, receipt, onChange,
                                       required = false, testid = "receipt" }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/costing/project/${p.id}/receipts`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ file_id: data.file_id, name: data.name,
                 mime: data.mime, size: data.size });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="text-[10px]" data-testid={testid}>
      {receipt ? (
        <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1">
          <span className="text-emerald-800 truncate">📎 {receipt.name}</span>
          <button type="button" onClick={() => onChange(null)}
            className="text-emerald-800 hover:text-red-700 ml-2"
            data-testid={`${testid}-clear`}>
            Remove
          </button>
        </div>
      ) : (
        <label className={`flex items-center justify-center border border-dashed rounded-sm px-2 py-1 cursor-pointer ${required ? "border-orange-400 bg-orange-50 text-orange-700" : "border-slate-300 text-slate-500"} ${busy ? "opacity-50" : "hover:bg-slate-50"}`}>
          <input ref={inputRef} type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
            className="hidden" onChange={upload} disabled={busy}
            data-testid={`${testid}-input`} />
          {busy ? "Uploading…" : (required ? "Attach receipt (required)" : "Attach receipt (optional)")}
        </label>
      )}
      {error && <div className="text-red-600 mt-0.5">{error}</div>}
    </div>
  );
}
